import apiClient from './client';
import type {
  SubscriptionInfo,
  CheckoutResponse,
  PortalResponse,
  ActivationKey,
  RedeemKeyResponse,
  WithdrawalResponse,
  BillingOverview,
  UserBillingList,
  SubscriptionEventList,
  InvoiceItem,
} from '../types/billing';

/** Create a Stripe Checkout session and return the hosted checkout URL. */
export async function createCheckoutSession(
  plan: 'premium_monthly' | 'premium_yearly',
): Promise<CheckoutResponse> {
  const { data } = await apiClient.post<CheckoutResponse>(
    '/api/billing/create-checkout-session',
    { plan },
  );
  return data;
}

/** Fetch the current user's subscription info. */
export async function getSubscription(): Promise<SubscriptionInfo> {
  const { data } = await apiClient.get<SubscriptionInfo>('/api/billing/subscription');
  return data;
}

/** Cancel the current subscription at period end. */
export async function cancelSubscription(): Promise<void> {
  await apiClient.post('/api/billing/cancel');
}

/** Request EU 14-day withdrawal (droit de retractation). */
export async function requestWithdrawal(): Promise<WithdrawalResponse> {
  const { data } = await apiClient.post<WithdrawalResponse>(
    '/api/billing/request-withdrawal',
  );
  return data;
}

/** Create a Stripe Customer Portal session for self-service billing management. */
export async function createPortalSession(): Promise<PortalResponse> {
  const { data } = await apiClient.post<PortalResponse>(
    '/api/billing/create-portal-session',
  );
  return data;
}

/** Fetch the current user's invoice history. */
export async function getInvoices(limit = 12): Promise<{ invoices: InvoiceItem[] }> {
  const { data } = await apiClient.get<{ invoices: InvoiceItem[] }>('/api/billing/invoices', { params: { limit } });
  return data;
}

/* ── Per-Certification Subscriptions ─────────────────────────────────────── */

export interface CertSubscription {
  id: number;
  certification_code: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  created_at: string | null;
}

export interface CertSubscriptionStatus {
  certification_code: string;
  has_subscription: boolean;
  status: string;
  current_period_end?: string | null;
  price_cents: number;
  currency: string;
}

/** Create a per-certification checkout session. */
export async function createCertCheckoutSession(
  certificationCode: string,
): Promise<CheckoutResponse> {
  const { data } = await apiClient.post<CheckoutResponse>(
    '/api/billing/create-cert-checkout',
    null,
    { params: { certification_code: certificationCode } },
  );
  return data;
}

/** List all per-certification subscriptions. */
export async function getCertSubscriptions(): Promise<CertSubscription[]> {
  const { data } = await apiClient.get<CertSubscription[]>('/api/billing/cert-subscriptions');
  return data;
}

/** Get per-certification subscription status. */
export async function getCertSubscriptionStatus(
  certificationCode: string,
): Promise<CertSubscriptionStatus> {
  const { data } = await apiClient.get<CertSubscriptionStatus>(
    `/api/billing/cert-subscription/${certificationCode}`,
  );
  return data;
}

/** Cancel a per-certification subscription. */
export async function cancelCertSubscription(certificationCode: string): Promise<{ status: string; message: string }> {
  const { data } = await apiClient.post<{ status: string; message: string }>(
    `/api/billing/cancel-cert-subscription/${certificationCode}`,
  );
  return data;
}

/* ── Entitlements ───────────────────────────────────────────────────────── */

export interface CertSubscriptionEntitlement {
  certification_code: string;
  status: string;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  created_at: string | null;
}

export interface EntitlementsResponse {
  global_plan: string;
  is_global_premium: boolean;
  is_admin: boolean;
  accessible_certifications: string[];
  can_exam_mode: string[];
  cert_subscriptions: CertSubscriptionEntitlement[];
}

/** Fetch the current user's entitlements (global + per-cert access). */
export async function getEntitlements(): Promise<EntitlementsResponse> {
  const { data } = await apiClient.get<EntitlementsResponse>('/api/entitlements');
  return data;
}

/** Request EU 14-day withdrawal for a per-certification subscription. */
export async function requestCertWithdrawal(
  certificationCode: string,
): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(
    '/api/billing/request-cert-withdrawal',
    null,
    { params: { certification_code: certificationCode } },
  );
  return data;
}

/* ── Activation Keys ─────────────────────────────────────────────────────── */

/** Redeem an activation key (user-facing). */
export async function redeemActivationKey(key: string): Promise<RedeemKeyResponse> {
  const { data } = await apiClient.post<RedeemKeyResponse>(
    '/api/billing/redeem-key',
    { key },
  );
  return data;
}

/** Create a new activation key (admin). */
export async function createActivationKey(payload: {
  plan?: string;
  duration_days?: number;
  note?: string;
  expires_at?: string;
}): Promise<ActivationKey> {
  const { data } = await apiClient.post<ActivationKey>(
    '/api/billing/activation-keys',
    payload,
  );
  return data;
}

/** List all activation keys (admin). */
export async function listActivationKeys(): Promise<ActivationKey[]> {
  const { data } = await apiClient.get<ActivationKey[]>('/api/billing/activation-keys');
  return data;
}

/** Deactivate an activation key (admin). */
export async function deactivateActivationKey(keyId: number): Promise<void> {
  await apiClient.delete(`/api/billing/activation-keys/${keyId}`);
}

/* ── Admin Billing Dashboard ─────────────────────────────────────────────── */

/** Fetch billing/finance overview KPIs (admin). */
export async function fetchBillingOverview(): Promise<BillingOverview> {
  const { data } = await apiClient.get<BillingOverview>('/api/admin/billing/overview');
  return data;
}

/** Fetch paginated user billing list (admin). */
export async function fetchBillingUsers(params?: {
  search?: string;
  plan?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<UserBillingList> {
  const { data } = await apiClient.get<UserBillingList>('/api/admin/billing/users', { params });
  return data;
}

/** Fetch paginated subscription events (admin). */
export async function fetchBillingEvents(params?: {
  event_type?: string;
  user_id?: number;
  limit?: number;
  offset?: number;
}): Promise<SubscriptionEventList> {
  const { data } = await apiClient.get<SubscriptionEventList>('/api/admin/billing/events', { params });
  return data;
}
